import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  useTransition,
} from "react";
import { Box, Flex, Text, useBreakpointValue } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import AppSelect from "@/features/line-sheet-sets/app-select";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useGetPathPickCounts from "@/features/pathpick/hooks/query/useGetPathPickCounts";
import useGetPathPickBasketProducts from "@/features/pathpick/features/basket/hooks/query/useGetPathPickBasketProducts";
import useGetPathPickBasketCounts from "@/features/pathpick/features/basket/hooks/query/useGetPathPickBasketCounts";
import usePathPickBasketFilter from "@/features/pathpick/features/basket/hooks/usePathPickBasketFilter";
import usePathPickBasketSelection from "@/features/pathpick/features/basket/hooks/usePathPickBasketSelection";
import useRemovePathPickBasketItems from "@/features/pathpick/features/basket/hooks/useRemovePathPickBasketItems";
import useClearPathPickBasket from "@/features/pathpick/features/basket/hooks/mutation/useClearPathPickBasket";
import PathPickBasketClearAllConfirmModal from "@/features/pathpick/features/basket/components/list/PathPickBasketClearAllConfirmModal";
import PathPickBasketListFilter from "@/features/pathpick/features/basket/components/list/PathPickBasketListFilter";
import PathPickBasketSearchInput from "@/features/pathpick/features/basket/components/list/PathPickBasketSearchInput";
import {
  PathPickBasketSortOption,
  PathPickBasketTab,
} from "@/features/pathpick/features/basket/types";
import { BASKET_SORT_LABEL_KEYS } from "@/features/pathpick/features/basket/constants";
import PathPickProductCardViewSettingsButton from "@/features/pathpick/components/product/PathPickProductCardViewSettingsButton";
import usePathPickProductCardView from "@/features/pathpick/hooks/usePathPickProductCardView";
import usePathPickAllResultIds from "@/features/pathpick/hooks/usePathPickAllResultIds";
import PathPickSelectAllResultsCheckBox from "@/features/pathpick/components/ui/PathPickSelectAllResultsCheckBox";
import { toPathPickProductKey } from "@/features/pathpick/utils";
import PathPickFilterToggleButton from "@/features/pathpick/components/ui/PathPickFilterToggleButton";
import PathPickBasketListFilterModal, {
  countPathPickBasketFilterAxes,
} from "@/features/pathpick/features/basket/components/list/PathPickBasketListFilterModal";

// 담은 상품 리스트 헤더 — 요약/상태 탭/검색/일괄 액션(전체선택·선택삭제·비우기)/KREAM 기준 토글
function PathPickBasketListHeader() {
  const { t, tTitle } = useI18nHelper();
  // 좁은 폭에서는 라벨을 짧은 문구로 바꾼다. 체크박스 라벨은 문자열 prop이라
  // CSS display 분기 대신 브레이크포인트 값으로 키를 고른다.
  const isNarrow = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );
  // 요약의 "총 N개"는 탭 필터와 무관한 담은 상품 전체 수다.
  const { pathPickCounts } = useGetPathPickCounts();
  const basketTotalCount = pathPickCounts?.basket.totalCount ?? 0;
  const { items, totalCount, requestParams } = useGetPathPickBasketProducts();
  const { pathPickBasketCounts } = useGetPathPickBasketCounts();
  const {
    filter,
    tab,
    sort,
    sortOptions,
    marketPriceBasis,
    applyQuery,
    changeTab,
    changeSort,
    changeMarketPriceBasis,
  } = usePathPickBasketFilter();
  // 비교 표시를 끄면 비교 기준과 마진 정렬을 함께 감춘다 — 카드에 보이지도 않는 값으로
  // 목록을 정렬하게 두면 결과를 설명할 수 없다.
  const { isComparisonVisible } = usePathPickProductCardView();
  const {
    selectedProducts,
    selectedCount,
    toggleAllOnPage,
    selectResults,
    removeProducts,
    clear,
  } = usePathPickBasketSelection();
  const removeItems = useRemovePathPickBasketItems();
  const { mutate: clearBasket, isPending: isClearingBasket } =
    useClearPathPickBasket();

  const [isClearAllConfirmOpen, setIsClearAllConfirmOpen] =
    useState<boolean>(false);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  // 필터 패널은 memo로 끊겨 있어 닫기 콜백 참조가 고정돼야 한다.
  const closeFilter = useCallback(() => setIsFilterOpen(false), []);
  // 모바일에서는 표시·정렬 옵션과 필터 패널 펼침 대신 필터 모달 하나로 고른다.
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>(filter.query ?? "");

  // 쿼리스트링(query)이 외부에서 바뀌면(뒤로가기 등) 입력창을 동기화한다.
  // (입력창 글자 자체는 자동완성 컴포넌트가 가지고, 여기 searchValue 는 검색 버튼용 사본이다)
  useEffect(() => {
    setSearchValue(filter.query ?? "");
  }, [filter.query]);

  // 탭이 바뀌면 선택을 비운다 — 탭마다 목록이 달라 이전 탭의 선택이 남으면
  // 화면에 보이지도 않는 상품이 선택 개수와 일괄 액션(선택 삭제/신청/주문)에 섞인다.
  // 탭 버튼 클릭이 아니라 tab 값 변화를 보는 이유는 뒤로가기 등 URL 변경까지 잡기 위해서다.
  const prevTabRef = useRef(tab);
  useEffect(() => {
    if (prevTabRef.current === tab) return;
    prevTabRef.current = tab;
    clear();
  }, [tab, clear]);

  // 전체 선택은 "현재 페이지 목록" 기준. 선택 자체는 페이지 이동에는 유지되고 탭 전환에만 초기화된다.
  // 담은 상품은 모두 선택 대상이라 목록이 비었을 때만 이 페이지 선택을 비활성화한다.
  const isPageSelectable = items.length > 0;
  const pageSelectedCount = items.filter((item) =>
    selectedProducts.has(toPathPickProductKey(item.productType, item.id))
  ).length;
  const isAllSelected = items.length > 0 && pageSelectedCount === items.length;
  const isIndeterminate = pageSelectedCount > 0 && !isAllSelected;

  // 검색 결과 전체 선택 — 목록과 같은 조회 조건(requestParams)으로 id·유형·판매 가능 여부를 미리 받아
  // "전체 n개 선택"의 n으로 쓴다. 1만 건(ids_only 상한)을 넘어도 받은 1만 건까지는 선택할 수 있다.
  // 규칙은 상품 찾기와 같다. requestParams에 탭 조건이 들어 있어 모든 탭에서 현재 탭 결과 기준으로 동작한다.
  const {
    resultIds,
    isTruncated: isAllResultsTruncated,
    isFetching: isFetchingResultIds,
  } = usePathPickAllResultIds(requestParams, { enabled: true });
  const canSelectAllResults = resultIds !== null && resultIds.length > 0;
  const isAllResultsSelected =
    resultIds !== null &&
    resultIds.length > 0 &&
    resultIds.every(({ id, productType }) =>
      selectedProducts.has(toPathPickProductKey(productType, id))
    );
  // 일부만(이 페이지 선택·카드 개별 선택) 골랐으면 "-"로 보여주고, 누르면 전체를 선택한다.
  const isAllResultsIndeterminate = !isAllResultsSelected && selectedCount > 0;

  // 전체 선택/해제는 페이지의 카드 전부를 다시 그려 무겁다 — transition으로 미뤄
  // 그동안 체크박스 위 스피너가 먼저 뜨게 한다(조회 중과 같은 표기).
  const [isApplyingSelection, startSelectionTransition] = useTransition();
  const isAllResultsBusy = isFetchingResultIds || isApplyingSelection;

  const handleToggleAllResults = () => {
    if (isAllResultsBusy || !resultIds) {
      return;
    }
    if (isAllResultsSelected) {
      startSelectionTransition(() => {
        removeProducts(
          resultIds.map(({ id, productType }) => ({ id, type: productType }))
        );
      });
      return;
    }
    startSelectionTransition(() => {
      selectResults(resultIds);
    });
  };

  const handleSearch = () => {
    applyQuery(searchValue.trim() || undefined);
  };

  const handleDeleteSelected = () => {
    removeItems(
      Array.from(selectedProducts.values(), ({ id, type }) => ({ id, type }))
    );
  };

  const handleConfirmClearAll = () => {
    clearBasket(undefined, {
      onSuccess: () => {
        clear();
        setIsClearAllConfirmOpen(false);
      },
    });
  };

  // 정렬 옵션은 탭별로 다르다(최근 담은 순 / 최근 신청순 / 최근 주문순 + 마진순·거래순).
  const sortSelectOptions: { name: string; value: PathPickBasketSortOption }[] =
    sortOptions.map((option) => ({
      name: t(BASKET_SORT_LABEL_KEYS[option]),
      value: option,
    }));

  // 탭 배지 수 — 신청과 주문은 겹칠 수 있어(신청한 뒤 주문) 셋을 더해도 총 N개가 되지 않는다.
  const tabs: { id: PathPickBasketTab; label: string; count: number }[] = [
    {
      id: "pending",
      label: t("path_pick:basket.list.tab_pending"),
      // 작업 전 탭의 서버 필드명은 notWorked다.
      count: pathPickBasketCounts?.notWorked ?? 0,
    },
    {
      id: "requested",
      label: t("path_pick:basket.list.tab_requested"),
      count: pathPickBasketCounts?.requested ?? 0,
    },
    {
      id: "ordered",
      label: t("path_pick:basket.list.tab_ordered"),
      count: pathPickBasketCounts?.ordered ?? 0,
    },
  ];

  return (
    <Flex flexDirection={"column"} gap={"12px"}>
      <Flex flexDirection={"column"} gap={"2px"}>
        <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
          {t("path_pick:basket.list.label")}
        </Text>
        {/* 모바일에서는 헤더 높이를 줄이려고 요약을 감춘다(선택 수는 플로팅 바에 나온다). */}
        <Text
          display={{ base: "none", md: "block" }}
          textStyle={"body-lg-bold"}
          color={"#1D1D1B"}
        >
          {t("path_pick:basket.list.summary", {
            total: basketTotalCount,
            selected: selectedCount,
          })}
        </Text>
      </Flex>

      <Flex flexDirection={"column"} gap={"8px"}>
        {/* 밑줄 탭 — 좁은 폭에서는 탭이 여러 줄로 접히는 대신 한 줄 가로 스크롤로 둔다. */}
        <Flex
          role={"tablist"}
          gap={"24px"}
          borderBottom={"1px solid #EFEFED"}
          overflowX={{ base: "auto", md: "visible" }}
          sx={{
            "::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          }}
        >
          {tabs.map((item) => {
            const isSelected = tab === item.id;
            return (
              <Flex
                key={item.id}
                as={"button"}
                role={"tab"}
                aria-selected={isSelected}
                alignItems={"center"}
                gap={"6px"}
                paddingX={"4px"}
                paddingY={"10px"}
                flexShrink={0}
                // 선택 표시선이 컨테이너 구분선 위에 겹치도록 1px 내린다.
                marginBottom={"-1px"}
                borderBottom={"2px solid"}
                borderBottomColor={isSelected ? "#1272EF" : "transparent"}
                color={isSelected ? "#1272EF" : "#8F8F8C"}
                _hover={isSelected ? undefined : { color: "#444440" }}
                onClick={() => changeTab(item.id)}
              >
                <Text
                  textStyle={isSelected ? "body-md-bold" : "body-md-medium"}
                  whiteSpace={"nowrap"}
                >
                  {item.label}
                </Text>
                <Text
                  textStyle={"caption-bold"}
                  minWidth={"20px"}
                  paddingX={"6px"}
                  borderRadius={"10px"}
                  textAlign={"center"}
                  background={isSelected ? "#EAF2FE" : "#F2F2F0"}
                  color={isSelected ? "#1272EF" : "#8F8F8C"}
                >
                  {item.count}
                </Text>
              </Flex>
            );
          })}
        </Flex>
        <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
          {t(`path_pick:basket.list.tab_${tab}_desc`)}
        </Text>
      </Flex>

      {/* 조회 조건 줄 — 왼쪽 검색어·필터 입력, 오른쪽 표시·정렬 옵션.
          모바일에서는 검색 버튼·필터 펼침·표시·정렬 옵션 대신 검색창 옆 필터 버튼 하나로 모은다
          (검색은 키보드 검색으로 제출, 나머지는 필터 모달에서 고른다). */}
      <Flex justifyContent={"space-between"} alignItems={"center"} gap={"8px"}>
        <Flex
          gap={"8px"}
          alignItems={"center"}
          flexWrap={"wrap"}
          flex={{ base: 1, md: "none" }}
          minWidth={0}
        >
          {/* stretch — 필터 버튼이 검색창 실제 높이를 따라간다. 모바일 기기에서는 전역 CSS
              (globals.css의 body.is-mobile-device input min-height: 40px)가 입력칸을 키운다. */}
          <Flex
            gap={"8px"}
            alignItems={"stretch"}
            width={{ base: "100%", md: "auto" }}
          >
            {/* 검색창의 width는 문자열만 받아 반응형 폭은 바깥 Box가 맡는다.
              모바일에서는 필터 버튼을 뺀 나머지 폭을 검색창이 다 쓴다. */}
            <Box
              flex={{ base: 1, md: "none" }}
              minWidth={0}
              width={{ base: "auto", md: "320px" }}
            >
              <PathPickBasketSearchInput
                onChange={setSearchValue}
                onSubmit={handleSearch}
              />
            </Box>
            <PathPickFilterToggleButton
              appliedCount={countPathPickBasketFilterAxes(filter, {
                isDefaultSort: sort === sortOptions[0],
                marketPriceBasis,
                isComparisonVisible,
              })}
              onClick={() => setIsMobileFilterOpen(true)}
              height={"auto"}
            />
          </Flex>
          {/* AppButton은 Tooltip으로 감싸여 있어 display 분기는 바깥 Flex에 건다. */}
          <Flex display={{ base: "none", md: "flex" }} gap={"8px"}>
            <AppButton
              variant={"primary"}
              width={"80px"}
              onClick={handleSearch}
            >
              <Flex gap={"4px"} alignItems={"center"}>
                <CustomIcon
                  src={"search"}
                  width={16}
                  height={16}
                  color={"#FFFFFF"}
                />
                <Text textStyle={"body-sm-bold"}>
                  {tTitle("common:search")}
                </Text>
              </Flex>
            </AppButton>
            <AppButton
              variant={"primary"}
              width={"80px"}
              onClick={() => setIsFilterOpen((prev) => !prev)}
            >
              <Flex gap={"4px"} alignItems={"center"}>
                <Text textStyle={"body-sm-bold"}>
                  {t("path_pick:filter.title")}
                </Text>
                <CustomIcon
                  src={isFilterOpen ? "chevron-up" : "chevron-down"}
                  width={16}
                  height={16}
                  color={"#FFFFFF"}
                />
              </Flex>
            </AppButton>
          </Flex>
          {/* 모바일에서는 검색창 아래 한 줄을 차지해 감춘다. */}
          <Text
            display={{ base: "none", md: "block" }}
            textStyle={"body-sm-medium"}
            color={"#8F8F8C"}
          >
            {t("path_pick:basket.list.search_result", { count: totalCount })}
          </Text>
        </Flex>
        {/* 표시·정렬 옵션 — 비교 기준·정렬은 불러온 목록을 어떻게 보여줄지에 대한 것이다.
            모바일에서는 필터 모달로 옮겨 감춘다. */}
        <Flex
          display={{ base: "none", md: "flex" }}
          gap={"8px"}
          alignItems={"center"}
          flexShrink={0}
        >
          {isComparisonVisible && (
            <Flex gap={"4px"} alignItems={"center"}>
              <Text
                textStyle={"body-sm-bold"}
                color={"#444440"}
                marginRight={"4px"}
              >
                {t("path_pick:filter.market_basis")}
              </Text>
              <AppButton
                variant={"white"}
                isActive={marketPriceBasis === "bidPrice"}
                onClick={() => changeMarketPriceBasis("bidPrice")}
                width={"100px"}
              >
                <Text textStyle={"body-sm-bold"}>
                  {t("path_pick:filter.basis_bid")}
                </Text>
              </AppButton>
              <AppButton
                variant={"white"}
                isActive={marketPriceBasis === "tradePrice"}
                onClick={() => changeMarketPriceBasis("tradePrice")}
                width={"100px"}
              >
                <Text textStyle={"body-sm-bold"}>
                  {t("path_pick:filter.basis_trade")}
                </Text>
              </AppButton>
            </Flex>
          )}
          {/* 비교 표시를 끄면 마진·거래 정렬이 사라지므로, URL에 남은 정렬은 탭 디폴트로 되돌린다. */}
          <PathPickProductCardViewSettingsButton
            onComparisonHidden={() => changeSort(undefined)}
          />
          {/* 정렬 — 모든 값이 order_by로 조회에 반영된다. */}
          <AppSelect<PathPickBasketSortOption>
            name={sortSelectOptions[0].name}
            value={sort}
            options={sortSelectOptions}
            isClearable={false}
            width={"150px"}
            onSelect={changeSort}
          />
        </Flex>
      </Flex>

      {/* 브랜드 목록(서스펜스 쿼리)을 기다리는 동안 페이지 전체가 스피너로 바뀌지 않도록
          페이지 루트 경계 대신 여기서 국소 경계를 둔다. */}
      <Suspense fallback={null}>
        <PathPickBasketListFilter isOpen={isFilterOpen} onClose={closeFilter} />
        {isMobileFilterOpen && (
          <PathPickBasketListFilterModal
            isOpen
            onClose={() => setIsMobileFilterOpen(false)}
          />
        )}
      </Suspense>

      {/* 현재 목록 조작 줄 — 선택 액션만 둔다(표시·정렬 옵션은 조회 조건 줄 우측). */}
      <Flex alignItems={"center"} gap={"16px"}>
        {/* 선택 체크박스는 상품 찾기와 같이 보더 없이 16px 간격으로 둔다. */}
        <Flex gap={"16px"} alignItems={"center"}>
          <AppCheckBox
            size={"small"}
            isChecked={isAllSelected}
            isIndeterminate={isIndeterminate}
            isDisabled={!isPageSelectable}
            onChange={() => toggleAllOnPage(items)}
            name={t("path_pick:basket.list.select_page")}
            labelFontSize={"16px"}
          />
          {canSelectAllResults && (
            <PathPickSelectAllResultsCheckBox
              count={resultIds.length}
              isTruncated={isAllResultsTruncated}
              isChecked={isAllResultsSelected}
              isIndeterminate={isAllResultsIndeterminate}
              isBusy={isAllResultsBusy}
              onToggle={handleToggleAllResults}
            />
          )}
        </Flex>
        {/* 모바일에서는 플로팅 선택 바 좌측 하단으로 옮긴다. */}
        <Text
          as={"button"}
          display={{ base: "none", md: "inline-block" }}
          textStyle={"body-sm-medium"}
          color={selectedCount > 0 ? "#8F8F8C" : "#D4D4D1"}
          cursor={selectedCount > 0 ? "pointer" : "not-allowed"}
          onClick={() => selectedCount > 0 && handleDeleteSelected()}
          _hover={selectedCount > 0 ? { color: "#444440" } : undefined}
        >
          {t("path_pick:basket.list.delete_selected")}
        </Text>
        <AppButton
          variant={"white"}
          onClick={() => setIsClearAllConfirmOpen(true)}
          size={"small"}
        >
          <Text textStyle={"body-sm-bold"} color={"#EE7D7A"}>
            {t(
              isNarrow
                ? "path_pick:basket.list.clear_all_short"
                : "path_pick:basket.list.clear_all"
            )}
          </Text>
        </AppButton>
      </Flex>

      {isClearAllConfirmOpen && (
        <PathPickBasketClearAllConfirmModal
          isOpen
          onClose={() => setIsClearAllConfirmOpen(false)}
          totalCount={basketTotalCount}
          isLoading={isClearingBasket}
          onConfirm={handleConfirmClearAll}
        />
      )}
    </Flex>
  );
}

export default PathPickBasketListHeader;
